import type {
  Policy,
  PolicyCoinsuranceType,
  PolicyListStatus,
  PolicyParty,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { formatCivilDate, formatDocument, formatMoney } from '@/shared/lib/format';

/**
 * Apoio da apolice completa (P-APO-1): os filtros do painel "Apolices" do OnPoint e
 * os textos que a lista e o detalhe repetem.
 */

/** Filtros do painel "Apolices", nas duas secoes do OnPoint. */
export interface PolicyFilters {
  number: string;
  status: PolicyListStatus | '';
  start_date: string;
  end_date: string;
  coinsurance_type: PolicyCoinsuranceType | '';
  big_risk: boolean;
  monitoring: boolean;
  step_in: boolean;
  policyholder_name: string;
  policyholder_document: string;
  insured_name: string;
  insured_document: string;
  broker_name: string;
}

export const filtrosDeApoliceVazios: PolicyFilters = {
  number: '',
  status: '',
  start_date: '',
  end_date: '',
  coinsurance_type: '',
  big_risk: false,
  monitoring: false,
  step_in: false,
  policyholder_name: '',
  policyholder_document: '',
  insured_name: '',
  insured_document: '',
  broker_name: '',
};

/** Situacoes do filtro, na ordem do OnPoint. */
export const situacoesDaApolice: PolicyListStatus[] = [
  'aguardando_emissao',
  'emitida',
  'cancelada',
  'vencida',
  'rejeitada',
];

/** As opcoes do tipo de cosseguro do OnPoint estao a confirmar (APO-034). */
export const tiposDeCosseguro: { value: PolicyCoinsuranceType; label: string }[] = [
  { value: 'cedido', label: 'Cosseguro cedido' },
  { value: 'aceito', label: 'Cosseguro aceito' },
];

export function contaFiltros(filtros: PolicyFilters): number {
  return Object.entries(filtros).filter(([, valor]) => valor !== '' && valor !== false)
    .length;
}

/**
 * A lista de "Aguardando Emissao" vem das operacoes em emissao, que so entendem os
 * filtros de tomador, corretor e caixas. Com outro filtro pedido, ela nao entra.
 */
export function pendentesEntram(filtros: PolicyFilters): boolean {
  if (filtros.status !== '' && filtros.status !== 'aguardando_emissao') return false;
  return (
    filtros.number === '' &&
    filtros.start_date === '' &&
    filtros.end_date === '' &&
    filtros.coinsurance_type === '' &&
    filtros.insured_name === '' &&
    filtros.insured_document === ''
  );
}

/** "Nao definido" do OnPoint para o campo vazio. */
export const NAO_DEFINIDO = 'Nao definido';

export function ouNaoDefinido(texto: string | null | undefined): string {
  return texto && texto.trim() !== '' ? texto : NAO_DEFINIDO;
}

/** Numero visivel da apolice: o da contraparte, ou o interno. */
export function numeroDaApolice(policy: Policy): string {
  return policy.external_number || String(policy.number);
}

/** Razao social do tomador como a apolice diz (fotografia), e o cadastro na falta. */
export function tomadorDaApolice(policy: Policy): { nome: string; documento: string } {
  const foto = policy.parties?.policyholder;
  return {
    nome: foto?.legal_name ?? policy.policyholder.label,
    documento: foto?.document ?? '',
  };
}

export function seguradoDaApolice(
  policy: Policy,
): { nome: string; documento: string } | null {
  const foto = policy.parties?.insured;
  if (foto) return { nome: foto.legal_name, documento: foto.document };
  if (policy.insured)
    return { nome: policy.insured.label, documento: policy.insured.sublabel };
  return null;
}

export function corretorDaApolice(
  policy: Policy,
): { nome: string; documento: string } | null {
  const foto = policy.parties?.broker;
  if (foto) return { nome: foto.legal_name, documento: foto.document };
  if (policy.broker) return { nome: policy.broker.label, documento: '' };
  return null;
}

export function documentoOuVazio(documento: string): string {
  return documento ? formatDocument(documento) : '';
}

/** Endereco da fotografia numa linha, como o OnPoint mostra. */
export function enderecoDaParte(parte: PolicyParty | null | undefined): string {
  if (!parte) return NAO_DEFINIDO;
  const e = parte.address;
  const rua = [e.street, e.number].filter(Boolean).join(', ');
  const cidade = [e.city, e.state].filter(Boolean).join('/');
  const cep = e.zip_code ? `CEP ${e.zip_code}` : '';
  const linha = [rua, e.complement, e.district, cidade, cep].filter(Boolean).join(', ');
  return linha || NAO_DEFINIDO;
}

export function vigenciaDe(
  inicio: string | null | undefined,
  fim: string | null | undefined,
) {
  if (!inicio && !fim) return NAO_DEFINIDO;
  return `${formatCivilDate(inicio)} - ${formatCivilDate(fim)}`;
}

/** Descricao da parcela, `<n>x de R$ <valor> s/ juros`, no formato brasileiro (D25). */
export function descricaoDaParcela(
  numero: number,
  valor: Money,
  juros?: Money,
): string {
  const semJuros = !juros || juros.amount === 0;
  return `${numero}x de ${formatMoney(valor)} ${semJuros ? 's/ juros' : `c/ juros de ${formatMoney(juros)}`}`;
}

export function simNao(valor: boolean | undefined): string {
  return valor ? 'Sim' : 'Nao';
}

/** Texto da dica (i) da vigencia atual do OnPoint (APO-016). */
export const DICA_DA_VIGENCIA =
  'Valida das 24h da data inicial ate as 24h da data final. A convencao de contagem esta pendente (PRC-008).';
